import { Navigate, Outlet, useLocation } from 'react-router'

import { paths } from '@/app/router/paths'
import { FullPageLoader } from '@/components/common/FullPageLoader'
import { env } from '@/config/env'
import NoAccessPage from '@/features/auth/pages/NoAccessPage'
import { useAuthStore } from '@/features/auth/store/auth.store'
import type { PortalRole } from '@/features/auth/types'

type ProtectedRouteProps = {
  /** When set, the user must hold one of these roles on top of being signed in. */
  roles?: PortalRole[]
}

export function ProtectedRoute({ roles }: ProtectedRouteProps) {
  const location = useLocation()
  const isHydrated = useAuthStore((state) => state.isHydrated)
  const hasEntraAccount = useAuthStore((state) => state.hasEntraAccount)
  const isAccessDenied = useAuthStore((state) => state.isAccessDenied)
  const user = useAuthStore((state) => state.user)

  // Redirecting before hydration would sign out every page refresh.
  if (!isHydrated) return <FullPageLoader />

  // Auth disabled: every route is open, roles included.
  if (!env.requireAuth) return <Outlet />

  if (!hasEntraAccount) {
    return <Navigate to={paths.auth.login} state={{ from: location }} replace />
  }

  // Signed in to the tenant, not on the portal's allow list. Not a redirect:
  // the sign-in page would hand them straight back here.
  if (isAccessDenied || !user) return <NoAccessPage />

  if (roles && !roles.some((role) => user.roles.includes(role))) {
    return <Navigate to={paths.projects.pipeline} replace />
  }

  return <Outlet />
}
